import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAdmin } from '../../utils/roleUtils';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import AdminPanel from '../../components/admin/AdminPanel';
import AdminBadge from '../../components/admin/AdminBadge';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import agencyAdminService from '../../services/agencyAdminService';
import { toast } from '../../utils/toast';
import { 
  ArrowLeft, Mail, Phone, Calendar, DollarSign, AlertCircle,
  UserCircle, TrendingUp, Loader2, Send
} from 'lucide-react';
import '../../styles/admin-theme.css';

const AgentDetail = () => {
  const { id: agentId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Check if user has agency admin access
  const hasAgencyAdminAccess = () => {
    return user && canAccessAdmin(user.role);
  };

  useEffect(() => {
    if (!hasAgencyAdminAccess()) {
      navigate('/agent/dashboard');
      return;
    }
    fetchAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, user, navigate]);

  const fetchAgent = async () => {
    try {
      setLoading(true);
      const response = await agencyAdminService.getAgents({ limit: 100 });
      
      if (response.success && response.data) {
        const foundAgent = response.data.find(a => a.id === agentId);
        setAgent(foundAgent || null);
      } else {
        setAgent(null);
      }
    } catch (error) {
      console.error('Error fetching agent:', error);
      setAgent(null);
    } finally {
      setLoading(false);
    }
  };

  const handleResendPasswordSetup = async () => {
    try {
      setSendingEmail(true);
      const response = await agencyAdminService.resendPasswordSetup(agentId);
      
      if (response.success) {
        if (response.emailSkipped) {
          toast.success('Password reset link generated. Email not sent (QA).');
        } else {
          toast.success('Password setup email sent successfully');
        }
      } else {
        toast.error('Failed to send password setup email');
      }
    } catch (error) {
      console.error('Error resending password setup:', error);
      
      // Handle specific error codes
      if (error.code === 'AGENT_NOT_FOUND') {
        toast.error('Agent not found');
      } else if (error.code === 'SEND_FAILED') {
        toast.error('Failed to send email. Please try again later.');
      } else if (error.code === 'FORBIDDEN' || error.code === 'UNAUTHORIZED') {
        toast.error('You do not have permission to perform this action.');
      } else {
        toast.error(error.message || 'Failed to resend password setup email');
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'inactive': return 'suspended';
      case 'pending': return 'pending';
      case 'denied': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Active';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getPerformanceBadgeVariant = (performance) => {
    switch (performance) {
      case 'excellent': return 'success';
      case 'good': return 'info';
      case 'average': return 'warning';
      case 'needs_improvement': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '—';
    }
  };

  const formatMetric = (value, formatter) => {
    if (value === null || value === undefined) {
      return '—';
    }
    return formatter(value);
  };

  if (!hasAgencyAdminAccess()) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto mb-4 admin-spinner"></div>
          <p className="text-gray-600">Loading agent details...</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <>
        <AdminPageHeader
          title="Agent Details"
          subtitle="View agent information and manage settings"
        />
        <AdminEmptyState
          icon={UserCircle}
          title="Agent not found"
          description="The agent you're looking for doesn't exist or you don't have permission to view it."
          action={
            <Link
              to="/agent/admin/agents"
              className="inline-flex items-center px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
            >
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to Agents
            </Link>
          }
        />
      </>
    );
  }

  const headerActions = [
    <Link
      key="back"
      to="/agent/admin/agents"
      className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      Back
    </Link>
  ];

  return (
    <>
      <AdminPageHeader
        title={agent.name || 'Agent Details'}
        subtitle="View agent information and manage settings"
        actions={headerActions}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Agent Information */}
          <AdminPanel title="Agent Information">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex flex-shrink-0 justify-center items-center w-16 h-16 text-xl font-semibold text-white bg-gradient-to-br from-blue-500 to-purple-600 rounded-full">
                  {(agent.name || 'A').split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{agent.name || 'Unknown'}</h2>
                  <div className="flex flex-wrap gap-3">
                    <AdminBadge variant={agent.role === 'Admin' ? 'info' : 'default'} size="md">
                      {agent.role || 'Agent'}
                    </AdminBadge>
                    <AdminBadge variant={getStatusBadgeVariant(agent.status)} size="md" dot>
                      {getStatusLabel(agent.status)}
                    </AdminBadge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-base text-gray-900">{agent.email || '—'}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Phone</label>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-base text-gray-900">{agent.phone || '—'}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Join Date</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-base text-gray-900">{formatDate(agent.joinDate)}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Login</label>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-base text-gray-900">{formatDate(agent.lastLogin)}</span>
                  </div>
                </div>
              </div>
            </div>
          </AdminPanel>

          {/* Performance Metrics */}
          <AdminPanel title="Performance Metrics">
            <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Total Trips</label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  <span className="text-2xl font-bold text-gray-900" title={agent.totalTrips === null || agent.totalTrips === undefined ? 'Not yet available' : undefined}>
                    {formatMetric(agent.totalTrips, (v) => v.toString())}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Total Revenue</label>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  <span className="text-2xl font-bold text-gray-900" title={agent.totalRevenue === null || agent.totalRevenue === undefined ? 'Not yet available' : 'Total commission earned'}>
                    {formatMetric(agent.totalRevenue, (v) => `$${v.toLocaleString()}`)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Conversion Rate</label>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                  <span className="text-2xl font-bold text-gray-900" title={agent.conversionRate === null || agent.conversionRate === undefined ? 'Not yet available' : 'Clients converted to customers'}>
                    {formatMetric(agent.conversionRate, (v) => `${v.toFixed(1)}%`)}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Performance</label>
                <div className="flex items-center gap-2">
                  {agent.performance ? (
                    <AdminBadge variant={getPerformanceBadgeVariant(agent.performance)} size="lg">
                      {agent.performance.replace('_', ' ')}
                    </AdminBadge>
                  ) : (
                    <span className="text-lg text-gray-400">—</span>
                  )}
                </div>
              </div>
            </div>
          </AdminPanel>
        </div>

        {/* Actions Sidebar */}
        <div className="space-y-6">
          <AdminPanel title="Actions">
            <div className="space-y-4">
              <button
                onClick={handleResendPasswordSetup}
                disabled={sendingEmail}
                className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Resend Password Setup Email
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500">
                Send a password setup email to this agent. They'll receive a link to set their password.
              </p>
            </div>
          </AdminPanel>
        </div>
      </div>
    </>
  );
};

export default AgentDetail;

